import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import { Banner, LiveBanner, Logo, offerBanner, PollsBanner, PresentationBanner, WebinnarBanner } from '../assets/images';
import { ActivityIcon, Analytics, ChatIcon, ContactIcon, Lead, ManagemnentIcon, MediaIcon, OfferIcons, PollsIcons, SalesIcon } from '../assets/icons';
import AuthService from '../services/auth.service';
import GoogleIconLogin from '../assets/icons/googleIconLogin';
import MicrosoftIconLogin from '../assets/icons/microsoftIconLogin';
import LinkdinIconLogin from '../assets/icons/linkdinIconLogin';
import { config } from '../config';

const EasywebinarLogin: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const banners = [
    LiveBanner,
    PresentationBanner,
    offerBanner,
    PollsBanner,
    WebinnarBanner,
  ];

  const tabs = [
    { id: 0, icon: <ChatIcon />, text: "Live Chat" },
    { id: 1, icon: <MediaIcon />, text: "Presentation Media" },
    { id: 2, icon: <OfferIcons />, text: "Push Offers" },
    { id: 3, icon: <PollsIcons />, text: "Push Polls" },
    { id: 4, icon: <Analytics />, text: "Webinar Analytics" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % tabs.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);

    if (!email || !password) {
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await AuthService.login({ email, password });
      navigate('/dashboard'); 
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const googleAuthUrl = config.sso_auth_url;
    const params = new URLSearchParams({
        client_id: config.sso_client_id, //todo - secret
        response_type: 'code',
        identity_provider: 'Google',
        scope: 'email openid profile',
        redirect_uri: `${config.auth_app_url}/dashboard`,  
        register: 'true'
    });

    window.location.href = `${googleAuthUrl}?${params.toString()}`;
  };

  return (
    <>
      <div className='login-authenticator-wrapper height-modification'>
        <header id="header" className="header-wrapper nav-down">
          <nav className="hide-on-med-and-down">
            <div className="nav-wrapper container flex">
              <a id="logo-container" className="brand-logo">
                <img src={Logo} alt="Logo" />
              </a>
            </div>
          </nav>
        </header>
        <section className="ew-signin-main flex">
          <div className="ew-signin-form login">
            <div className="signin-form">
              <h2 className='main-heading'>Welcome back!</h2>
              <p>Ready to take over the world?</p>

              {/* Error Message */}
              {error && <span className="error form-error-message">{error}</span>}

              {/* Login Form */}
              <form id="loginForm" onSubmit={handleSubmit}>
                <div className="signup-process-field">
                  <input
                    type="text"
                    id="inputUsername"
                    name="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder=""
                    className='floating-value'
                  />
                  <label htmlFor="inputUsername">Email ID / Username</label>
                  {isSubmitted && !email && (
                    <span className="error-custom-message error-username">
                      Please enter username.
                    </span>
                  )}
                </div>

                <div className="signup-process-field">
                  <input
                    type="password"
                    id="inputPassword"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder=""
                  />
                  <label htmlFor="inputPassword">Password</label>
                  {isSubmitted && !password && (
                    <span className="error-custom-message error-password">
                      Please enter password.
                    </span>
                  )}
                </div>

                <div className="forgot-password">
                  <a href="/forgot-password">Forgot your password?</a>
                </div>

                <Button 
                  variant="contained" 
                  type="submit"
                  className="x-large-btn" 
                  disabled={isLoading}
                  disableElevation 
                  sx={{width:"100%" ,textTransform:"capitalize"}}
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>

              {/* Create Account Section */}
              <div className="create-account">
                <p>Don't Have an Account?</p>
                <Link to="/signup" className="create-account-link">Create an account</Link>
              </div>

              <div className="platform-sigIn-wrapper">
                <span>Or Sign in with</span>
                <div className="box-container">
                  <div className="outer-box" onClick={handleGoogleSignIn}>
                    <GoogleIconLogin></GoogleIconLogin>
                  </div>
                  <div className="outer-box">
                    <MicrosoftIconLogin></MicrosoftIconLogin>
                  </div>
                  <div className="outer-box">
                    <LinkdinIconLogin></LinkdinIconLogin>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="ew-copyright flex">
                <span>&copy; 2025 All Rights Reserved</span>
                <ul className="flex">
                  <li>
                    <a href="https://easywebinar.com/refund-policy/" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  </li>
                  <li>
                    <a href="https://easywebinar.com/privacy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  </li>
                  <li>
                    <a href="https://easywebinar.com/cookies/" target="_blank" rel="noopener noreferrer">Security</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Marketing Section */}
          <div className="ew-signin-marketing">
            <div className="ew-easycast-intro center unset">
              <div className='heading-wrapper'>
                {/* green text */}
                <h4 className="text-uppercase heading"> Simplify. Scale. <span className='hightlighted-text'>Succeed</span></h4>

                {/* puple text */}
                {/* <h4 className="text-uppercase heading width-style">Webinars That Convert, <span className='hightlighted-text purple'>EasyCRM</span> That Closes</h4> */}

                {/* green text */}
                <span className='sub-heading green-text'>Create, automate, and optimize webinars with ease. Integrate with your favorite tools and unlock growth opportunities through real-time analytics.</span>

                {/* purple text */}
                {/* <span className='sub-heading purple-text'>EasyWebinar just got even better! Introducing EasyCRM, the ultimate tool for managing leads, nurturing relationships, and closing deals faster. Designed to work seamlessly with EasyWebinar, EasyCRM transforms your webinars into powerful conversion machines.</span> */}
              </div>

              {/* this is for purple text content for tabs */}
              {/* <div className='content-wrapper'>
                purple image
                <img src={Banner} alt="" className="banner-images"  />

                <div className='tab-component-wrapper'>
                  <div className='tabs'>
                    <Lead></Lead>
                    <span className='text'>Lead <br></br>
                    Management</span>
                  </div>

                  <div className='tabs'>
                    <ContactIcon></ContactIcon>
                    <span className='text'>Contact <br></br>
                    Management</span>
                  </div>
                  <div className='tabs'>
                    <SalesIcon></SalesIcon>
                    <span className='text'>Sales <br></br>
                    Pipeline</span>
                  </div>
                  <div className='tabs'>
                    <ManagemnentIcon></ManagemnentIcon>
                    <span className='text'>Team <br></br>
                    Management</span>
                  </div>
                  <div className='tabs'>
                    <ActivityIcon></ActivityIcon>
                    <span className='text'>Activity <br></br>
                    Management</span>
                  </div>
                </div>
                <div className="ew-copyright flex d-none">
                <span>&copy; 2024 All Rights Reserved</span>
                <ul className="flex">
                  <li>
                    <a href="https://easywebinar.com/refund-policy/" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  </li>
                  <li>
                    <a href="https://easywebinar.com/privacy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  </li>
                  <li>
                    <a href="https://easywebinar.com/cookies/" target="_blank" rel="noopener noreferrer">Security</a>
                  </li>
                </ul>
              </div>
              </div> */}

              {/* green text contents for tabs */}
              <div className="content-wrapper">
                <div>
                  <img src={banners[activeTab]} alt="Banner" className="banner-images" />
                </div>

                <div className="tab-component-wrapper">
                  {tabs.map((tab, index) => (
                    <div
                      key={tab.id}
                      className={`tabs ${activeTab === index ? "active" : ""}`}
                    >
                      {tab.icon}
                      <span className="text">{tab.text}</span>
                    </div>
                  ))}
                </div>


                <div className="ew-copyright flex d-none">
                  <span>&copy; 2024 All Rights Reserved</span>
                  <ul className="flex">
                    <li>
                      <a
                        href="https://easywebinar.com/refund-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://easywebinar.com/privacy/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://easywebinar.com/cookies/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Security
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default EasywebinarLogin;
